import React, { createContext, useCallback, useContext, useState } from 'react';

import { PaletteMode } from '@mui/material';

type ThemeModeContextType = {
  themeMode: PaletteMode;
  toggleTheme: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextType | null>(null);

type Props = {
  children: React.ReactNode;
  defaultMode: PaletteMode;
};

export const ThemeModeProvider: React.FC<Props> = ({
  children,
  defaultMode,
}) => {
  const [themeMode, setThemeMode] = useState<PaletteMode>(() => {
    const stored = localStorage.getItem('theme_mode');
    if (stored === 'light' || stored === 'dark') return stored;
    if (window.matchMedia?.('(prefers-color-scheme: light)').matches)
      return 'light';
    return defaultMode;
  });

  const toggleTheme = useCallback(() => {
    setThemeMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme_mode', next);
      return next;
    });
  }, []);

  return (
    <ThemeModeContext.Provider value={{ themeMode, toggleTheme }}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextType => {
  const context = useContext(ThemeModeContext);

  if (!context) {
    throw new Error('useThemeMode must be called within ThemeModeProvider');
  }

  return context;
};
